import { useState, useEffect, useCallback, useRef } from 'react'
import { getPurchasedSubscriptions, BENEFIT_DESKTOP_ACCESS } from 'services/Api/SubscriptionApi'

const SUBSCRIPTION_EXPIRY_KEY = 'okc_subscription_expiry_time'
const SUBSCRIPTION_LAST_CHECK_KEY = 'okc_subscription_last_check_date'

// Forces one authoritative server check per full page load. Resets on a real
// reload (the module re-initializes) and persists across in-app SPA navigations,
// so we re-verify on every reload without hitting the API on every route change.
let verifiedThisPageLoad = false

interface SubscriptionData {
  last_expired_sub: any
  active_sub: any
  future_subs: any[]
  active_mandate: any
}

interface UseSubscriptionReturn {
  hasActiveSubscription: boolean
  ready: boolean
  loading: boolean
  subscriptionData: SubscriptionData | null
  error: string | null
  expiryTime: number | null
  checkAndRefreshIfExpired: () => Promise<void>
}

export const getStoredExpiryTime = (): number | null => {
  if (typeof window === 'undefined') {
    return null
  }
  const stored = localStorage.getItem(SUBSCRIPTION_EXPIRY_KEY)
  return stored ? parseInt(stored, 10) : null
}

// Fired on window whenever the cached expiry is (re)written, so same-tab
// consumers (the premium icon) can re-evaluate without a page load
export const SUBSCRIPTION_STORED_EVENT = 'okc:subscription-stored'

const setStoredExpiryTime = (expiryTime: number | null): void => {
  if (typeof window === 'undefined') {
    return
  }
  if (expiryTime) {
    localStorage.setItem(SUBSCRIPTION_EXPIRY_KEY, expiryTime.toString())
  } else {
    localStorage.removeItem(SUBSCRIPTION_EXPIRY_KEY)
  }
  window.dispatchEvent(new Event(SUBSCRIPTION_STORED_EVENT))
}

export const isExpired = (expiryTime: number | null): boolean => {
  if (!expiryTime) {
    return true
  }
  const now = Math.floor(Date.now() / 1000)
  return now >= expiryTime
}
const getTodayKey = (): string => {
  const d = new Date()
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getLastCheckDate = (): string | null => {
  if (typeof window === 'undefined') {
    return null
  }
  return localStorage.getItem(SUBSCRIPTION_LAST_CHECK_KEY)
}

const setLastCheckDate = (date: string | null): void => {
  if (typeof window === 'undefined') {
    return
  }
  if (date) {
    localStorage.setItem(SUBSCRIPTION_LAST_CHECK_KEY, date)
  } else {
    localStorage.removeItem(SUBSCRIPTION_LAST_CHECK_KEY)
  }
}

export const checkedToday = (): boolean => getLastCheckDate() === getTodayKey()

export const useSubscription = (skipCheck = false): UseSubscriptionReturn => {
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false)
  const [ready, setReady] = useState(false)
  const [loading, setLoading] = useState(true)
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expiryTime, setExpiryTime] = useState<number | null>(null)
  const inFlightRef = useRef<Promise<void> | null>(null)

  const checkSubscription = useCallback((): Promise<void> => {
    if (inFlightRef.current) {
      return inFlightRef.current
    }

    const run = (async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await getPurchasedSubscriptions()
        const data: SubscriptionData = response.data

        const hasActiveSub = !!data?.active_sub?.benefits?.includes(BENEFIT_DESKTOP_ACCESS)
        const newExpiryTime = data?.active_sub?.expiry_time || null

        setHasActiveSubscription(hasActiveSub)
        setSubscriptionData(data)
        setExpiryTime(hasActiveSub ? newExpiryTime : null)
        setStoredExpiryTime(hasActiveSub ? newExpiryTime : null)
        setLastCheckDate(getTodayKey())
        setReady(true)
      } catch (err: any) {
        setError(err.message || 'Failed to check subscription')
        const storedExpiry = getStoredExpiryTime()
        if (storedExpiry && !isExpired(storedExpiry)) {
          setHasActiveSubscription(true)
          setExpiryTime(storedExpiry)
        } else {
          setHasActiveSubscription(false)
        }
        setReady(true)
      } finally {
        setLoading(false)
        inFlightRef.current = null
      }
    })()

    inFlightRef.current = run
    return run
  }, [])

  const checkAndRefreshIfExpired = useCallback(async () => {
    if (skipCheck) {
      return
    }

    const storedExpiry = getStoredExpiryTime()
    // Re-verify with the server whenever the cached expiry has lapsed OR we haven't
    // yet verified during this page load. checkSubscription() then rewrites
    // localStorage from the API response (expiry, or clears it when there's no
    // active sub), so a stale/tampered cached expiry can't keep granting access
    // across reloads. Within the same page load we trust that fresh result to
    // avoid an API call on every SPA navigation.
    if (isExpired(storedExpiry) || !verifiedThisPageLoad) {
      verifiedThisPageLoad = true
      await checkSubscription()
      return
    }

    // Already verified this page load and the cache is still valid → trust it.
    setHasActiveSubscription(true)
    setExpiryTime(storedExpiry)
    setReady(true)
    setLoading(false)
  }, [skipCheck, checkSubscription])

  useEffect(() => {
    if (skipCheck) {
      setHasActiveSubscription(true)
      setReady(true)
      setLoading(false)
      return
    }

    if (typeof window === 'undefined') {
      setHasActiveSubscription(false)
      setReady(true)
      setLoading(false)
      return
    }

    // Not skipping: move to a pending state and let checkAndRefreshIfExpired
    // (driven by the consumer) perform the single source-of-truth check. This
    // avoids the duplicate API call that previously fired from both here and the
    // consumer's navigation effect.
    setReady(false)
    setLoading(true)
  }, [skipCheck])

  return {
    hasActiveSubscription,
    ready,
    loading,
    subscriptionData,
    error,
    expiryTime,
    checkAndRefreshIfExpired
  }
}

export const clearStoredSubscription = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(SUBSCRIPTION_EXPIRY_KEY)
    localStorage.removeItem(SUBSCRIPTION_LAST_CHECK_KEY)
  }
}
